import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { createStore } from 'zustand';

import { Button, ButtonModel, DrawerContext } from '@notino/react-styleguide';
import { WishlistFiltrationGroupType } from '@notino/shared/definitions/types';

import { useWishlistStore } from '@containers/WishList/store';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import { trackApplyFiltration } from '../../tracking';
import { DrawerTitle } from '../DrawerTitle';
import messages from '../messages';

import { BrandsMultiSelectList } from './BrandsMultiSelectList';
import { FooterSelectedItems } from './FooterSelectedItems';
import { MultiSelectList } from './MultiSelectList';
import { FooterButtonWrapper, FooterWrapper } from './styled';

type Props = {
  groupType: WishlistFiltrationGroupType;
  onClose: () => void;
};

export const FiltrationDrawerHandler = ({ groupType, onClose }: Props) => {
  const isBrand = groupType === WishlistFiltrationGroupType.Brand;
  const filter = useWishlistStore((x) => x.filtration).find(
    (x) => x.groupType === groupType
  );
  const selectedNavValues = useWishlistStore((x) => x.selectedNavigationValues);
  const { setSelectedNavigationValues } = useWishlistStore((x) => x.actions);
  const { openDrawer } = DrawerContext.useDrawerContext();

  const initialSelectedItems = React.useRef(
    (filter?.items ?? []).filter((x) =>
      selectedNavValues.includes(x.navigationValue)
    )
  );

  const { getTimeFromInit } = useTrackingContext();

  const [store] = React.useState(
    createStore<{
      selectedItems: Array<{ name: string; navigationValue: number }>;
    }>(() => ({
      selectedItems: initialSelectedItems.current,
    }))
  );

  const handleApplyFilter = () => {
    const initialSelectedValues = initialSelectedItems.current.map(
      (x) => x.navigationValue
    );
    const selectedValues = store
      .getState()
      .selectedItems.map((x) => x.navigationValue);

    const addedValues = selectedValues.filter(
      (x) => !initialSelectedValues.includes(x)
    );
    const removedValues = initialSelectedValues.filter(
      (x) => !selectedValues.includes(x)
    );

    setSelectedNavigationValues([
      ...selectedNavValues.filter((x) => !removedValues.includes(x)),
      ...addedValues,
    ]);

    trackApplyFiltration({
      timing: getTimeFromInit(),
      name: filter?.groupName ?? '',
      type: groupType,
      filters: filter?.items.map((item) => item.navigationValue) ?? [],
    });
    onClose();
  };

  React.useEffect(() => {
    openDrawer(
      isBrand ? (
        <BrandsMultiSelectList store={store} groupType={groupType} />
      ) : (
        <MultiSelectList store={store} groupType={groupType} />
      ),
      {
        title: (
          <DrawerTitle
            name={filter?.groupName ?? ''}
            onRefreshButtonClick={() => store.setState({ selectedItems: [] })}
            afterClose={onClose}
          />
        ),
        footerContent: (
          <FooterWrapper>
            {isBrand && <FooterSelectedItems store={store} />}
            <FooterButtonWrapper>
              <Button
                buttonStyle={ButtonModel.Styles.primary}
                onClick={handleApplyFilter}
              >
                <FormattedMessage {...messages.show} />
              </Button>
            </FooterButtonWrapper>
          </FooterWrapper>
        ),
        onClose,
        initialPosition: isBrand ? 'top' : 'dynamic',
        showCloseIcon: false,
        showFade: true,
        isTitleBorderHidden: true,
        allowResize: false,
        hideScrollbar: true,
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
